import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const DeveloperAgreementPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Role Model AI Developer Submission Agreement - SELF Labs</title>
        <meta name="description" content="Role Model AI Developer Submission Agreement - Effective Date: April 1, 2025" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              📜 Role Model AI Developer Submission Agreement
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            <div className="prose prose-invert max-w-none mb-12">
              <p>This Developer Submission Agreement ("Agreement") is entered into by and between SELF Labs Inc. ("SELF Labs," "we," "us," or "our") and the individual developer or entity ("Developer," "you," or "your") submitting an application, tool, model, or service ("Submitted Application") for potential inclusion in the Role Model AI App Store.</p>
              <p>By submitting any application to SELF Labs for review, you agree to the following terms:</p>
            </div>

            <div className="space-y-12">
              {/* Submission and Review */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Submission and Review</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Developer may submit applications, AI agents, models, tools, or related services for inclusion in the Role Model AI App Store.</p>
                  <p>All submissions are subject to review, approval, rejection, suspension, or removal at SELF Labs' sole discretion.</p>
                  <p>Submission does not guarantee inclusion or listing within the App Store.</p>
                </div>
              </section>

              {/* Developer Warranties */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Developer Warranties and Representations</h2>
                <div className="prose prose-invert max-w-none">
                  <p>By submitting a Submitted Application, Developer represents and warrants that:</p>
                  <ul>
                    <li>You are the rightful owner, licensee, or authorized distributor of all components of the Submitted Application.</li>
                    <li>The Submitted Application does not infringe, violate, or misappropriate any intellectual property, privacy, publicity, or other rights of any third party.</li>
                    <li>The Submitted Application is free of malware, viruses, spyware, or any other harmful code.</li>
                    <li>The Submitted Application complies with all applicable laws, regulations, and SELF Labs standards.</li>
                    <li>You have obtained all necessary rights, permissions, licenses, and consents for the Submitted Application to be publicly distributed, installed, and operated.</li>
                  </ul>
                </div>
              </section>

              {/* Rights Granted */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Rights Granted to SELF Labs</h2>
                <div className="prose prose-invert max-w-none">
                  <p>By submitting a Submitted Application, Developer grants SELF Labs a non-exclusive, worldwide, royalty-free license to:</p>
                  <ul>
                    <li>Distribute, display, promote, and market the Submitted Application within the Role Model AI App Store.</li>
                    <li>Convert or package the Submitted Application for optimized installation on the SELF Phone and compatible devices (e.g., APK conversion if originally submitted as a web app).</li>
                    <li>Provide access to users for download, installation, and use under SELF Labs' platform policies.</li>
                  </ul>
                  <p>Developer retains all ownership rights to the Submitted Application.</p>
                </div>
              </section>

              {/* Approval and Removal */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Approval and Removal</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs reserves the right to approve, decline, suspend, or remove any Submitted Application at any time, with or without cause, and with or without notice.</p>
                  <p>Common reasons for removal may include (but are not limited to):</p>
                  <ul>
                    <li>Security vulnerabilities</li>
                    <li>Violation of user trust</li>
                    <li>Regulatory risks</li>
                    <li>Infringement claims</li>
                    <li>Deceptive or harmful practices</li>
                  </ul>
                  <p>No compensation or liability will be owed to Developer for removal decisions.</p>
                </div>
              </section>

              {/* Developer Support */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Developer Support and Updates</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Developer is responsible for supporting their Submitted Application, including:</p>
                  <ul>
                    <li>Addressing bugs or vulnerabilities</li>
                    <li>Providing version updates when necessary</li>
                    <li>Maintaining compatibility with evolving SELF Phone systems</li>
                  </ul>
                  <p>Failure to maintain Submitted Applications in working order may result in removal.</p>
                </div>
              </section>

              {/* Fees and Compensation */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Fees and Compensation</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Unless otherwise agreed upon in writing, SELF Labs does not pay developers directly for Submitted Applications.</p>
                  <p>Developers may offer their apps:</p>
                  <ul>
                    <li>Free to users</li>
                    <li>Paid (in ESSENCE, other tokens, or future app store credit frameworks — subject to future agreements)</li>
                  </ul>
                  <p>Revenue-sharing models, if introduced, will be offered through separate agreements.</p>
                </div>
              </section>

              {/* Indemnification */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Indemnification</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Developer agrees to indemnify, defend, and hold harmless SELF Labs, its officers, directors, employees, agents, and affiliates from and against any claims, damages, liabilities, costs, or expenses arising out of:</p>
                  <ul>
                    <li>Breach of this Agreement</li>
                    <li>Violation of third-party rights</li>
                    <li>Failure to comply with applicable laws</li>
                    <li>Harm caused by the Submitted Application</li>
                  </ul>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Limitation of Liability</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs shall not be liable for any indirect, incidental, consequential, punitive, or special damages arising from:</p>
                  <ul>
                    <li>Developer's participation in the Role Model AI App Store</li>
                    <li>Removal of Developer's Submitted Application</li>
                    <li>User interactions with Developer's Submitted Application</li>
                  </ul>
                  <p>Participation is voluntary and at Developer's own risk.</p>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Governing Law</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Agreement is governed by and construed in accordance with the laws of the State of Delaware, United States.</p>
                  <p>All disputes under this Agreement shall be resolved through binding arbitration in Wilmington, Delaware.</p>
                </div>
              </section>

              {/* Entire Agreement */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">10. Entire Agreement</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Agreement constitutes the complete understanding between Developer and SELF Labs regarding Submitted Applications.</p>
                  <p>No oral promises or prior communications alter the terms of this Agreement unless made in writing and signed by both parties.</p>
                </div>
              </section>

              {/* Acceptance */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">📋 Acceptance</h2>
                <div className="prose prose-invert max-w-none">
                  <p>By submitting any application, agent, or tool for listing in the Role Model AI App Store, Developer acknowledges that they have read, understood, and agreed to be legally bound by this Developer Submission Agreement.</p>
                </div>
              </section>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/reseller-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Reseller Agreement
                    </Link>
                    <Link to="/mesh-license" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Mesh License
                    </Link>
                    <Link to="/developer-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Developer Agreement
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperAgreementPage; 